/* eslint-disable camelcase */
/* eslint-disable react/no-danger */
import React, { useState } from "react";
import { SquareButton } from "@themuse/design-system";

import { useAppState } from "hooks/AppContext";
import { getJobTrackingProps } from "utils/tracking";
import { JobApplyButton } from "./JobApplyButton";
import { JWPlayerVideoModule } from "./JWPlayerVideoModule";
import { PerksAndBenefits } from "./PerksAndBenefits";
import { JobClientProvidedLocation } from "./JobClientProvidedLocation";
import { JobAlertsWrapper } from "./JobAlertsWrapper";

import styles from "./JobIndividualBody.module.scss";

const MemoizedPerksAndBenefits = React.memo(PerksAndBenefits);

export const JobIndividualBody = (props: {
    setIsOpen: (x: boolean) => void;
    isFrame?: boolean;
}) => {
    const { job, company } = useAppState();
    const {
        has_jwp_company_playlist,
        name: companyName = ""
    } = company;
    const {
        ats_info,
        compiled_contents = [],
        state: jobState = "",
        id: jobId,
        locations = [],
        has_jwp_hiring_manager_playlist = false,
        employmentTypes
    } = job;
    const {
        directApplyService = "",
        id: atsId = ""
    } = ats_info || {};
    const { setIsOpen, isFrame = false } = props;
    const [jobAlertsViewed, setJobAlertsViewed] = useState(false);
    const [successViewed, setSuccessViewed] = useState(false);

    const trackApplyProps = {
        ...getJobTrackingProps(job, company),
        placement_position: "desktop_bottom"
    };

    // Converts the values to a more readable format by removing underscores and returning title case.
    const employmentDisplayTypes = employmentTypes
        .map((x: string) => x.replace("_", " ").toLowerCase().replace(/\b\w/g, (s: string) => s.toUpperCase()))
        .join(", ");

    return (
        <section className={`${styles.jobBodyContainer} ${isFrame ? styles.framed : ""}`}>
            <article className={styles.jobBodyDescription}>
                {compiled_contents.map((x: string, index: number) => (
                    <React.Fragment key={x.slice(1, 32)}>
                        <div dangerouslySetInnerHTML={{ __html: x }} />
                        {/* Always after the first block, even if there is only one. */}
                        {index === 0 && (
                            <JobAlertsWrapper
                                job={job}
                                jobAlertViewed={jobAlertsViewed}
                                setJobAlertViewed={setJobAlertsViewed}
                                successViewed={successViewed}
                                setSuccessViewed={setSuccessViewed}
                            />
                        )}
                    </React.Fragment>
                ))}
            </article>
            <div className={styles.jobBodyFooter}>
                <JobClientProvidedLocation locations={locations} />
                <b>Job ID:</b>{" "}{atsId}
                <br />
                <b>Employment Type:</b>{" "}{employmentDisplayTypes}
            </div>
            <div className={`${styles.applyContainer} ${isFrame ? styles.framed : ""}`}>
                {jobState === "live" ? (
                    <JobApplyButton
                        className={`${styles.applyButton} ${isFrame ? styles.framed : ""}`}
                        jobId={jobId}
                        setIsOpen={setIsOpen}
                        isDirectApply={!!directApplyService}
                        trackingProps={trackApplyProps}
                    />
                ) : (
                    <>
                        <p className={styles.archivedJobMessage}>This job is no longer available.</p>
                        <SquareButton
                            className={`${styles.archiveButton} ${isFrame ? styles.framed : ""}`}
                            href="/search"
                            target="_blank"
                            color="mint"
                        >
                            Search all jobs
                        </SquareButton>
                    </>
                )}
            </div>
            { has_jwp_hiring_manager_playlist ? (
                <JWPlayerVideoModule
                    title="Meet the Hiring Manager"
                    description="Hear directly from the hiring manager about what it is like to work here."
                    tagName={jobId}
                />
            ) : null }
            <MemoizedPerksAndBenefits company={company} />
            { has_jwp_company_playlist ? (
                <JWPlayerVideoModule
                    title="Company Videos"
                    description={`Hear directly from employees about what it is like to work at ${companyName}.`}
                    tagName={`${companyName},final`}
                    excludeTag={true}
                />
            ) : null }
        </section>
    );
};
JobIndividualBody.displayName = "JobIndividualBody";
